/**
    * @description      : Billing page for renters to view billing history and payments
    * @author           : Saif
    * @group            : 
    * @created          : 04/11/2024 - 22:30:43
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/11/2024
    * - Author          : Saif
    * - Modification    : 
**/
import { useState, useEffect } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faReceipt,
  faCreditCard,
  faMoneyBill,
  faCalendarDays,
  faDownload,
  faEye,
  faSearch,
  faFilter
} from "@fortawesome/free-solid-svg-icons";
import { useGet } from "../../Api";
import CustomTable from "../../Components/CustomTable";
import CustomButton from "../../Components/CustomButton";
import "./style.css";

export const Billing = () => {
  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [billingStats, setBillingStats] = useState(null);
  
  const { ApiData: BillsData, loading: BillsLoading, error: BillsError, get: GetBills } = useGet(`/billing/history`);
  const { ApiData: StatsData, loading: StatsLoading, error: StatsError, get: GetStats } = useGet(`/billing/stats`);

  useEffect(() => {
    document.title = 'kvr User | Billing';
    GetBills();
    GetStats();
  }, []);

  useEffect(() => {
    if (BillsData) {
      setBills(BillsData?.data || []);
      setFilteredBills(BillsData?.data || []);
    }
  }, [BillsData]);

  useEffect(() => {
    if (StatsData) {
      setBillingStats(StatsData?.data);
    }
  }, [StatsData]);

  useEffect(() => {
    // Filter bills based on search term and status
    let filtered = bills;
    
    if (searchTerm) {
      filtered = filtered.filter(bill => 
        bill.bill_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bill.property_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(bill => bill.status === statusFilter);
    }
    
    setFilteredBills(filtered);
  }, [bills, searchTerm, statusFilter]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'paid': 'badge-success',
      'pending': 'badge-warning',
      'overdue': 'badge-danger',
      'cancelled': 'badge-secondary'
    };
    
    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleDownloadBill = (billId) => {
    // Implement download functionality
    console.log('Downloading bill:', billId);
  };

  const handleViewBill = (billId) => {
    // Implement view functionality
    console.log('Viewing bill:', billId);
  };

  const tableHeaders = [
    { key: 'bill_number', title: 'Bill #' },
    { key: 'property_name', title: 'Property' },
    { key: 'amount', title: 'Amount' },
    { key: 'due_date', title: 'Due Date' },
    { key: 'status', title: 'Status' },
    { key: 'actions', title: 'Actions' }
  ];

  return (
    <>
      <DashboardLayout>
        <div className="billing-container">
          <div className="row">
            <div className="col-12">
              <h2 className="page-title">
                <FontAwesomeIcon icon={faReceipt} className="me-2" />
                Billing
              </h2>
            </div>
          </div>

          {/* Billing Stats Cards */}
          {billingStats && (
            <div className="row mb-4">
              <div className="col-md-4 mb-3">
                <div className="billing-card total-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faMoneyBill} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">Total Paid</h3>
                    <p className="card-amount">
                      {formatCurrency(billingStats.total_paid || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-4 mb-3">
                <div className="billing-card pending-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faCalendarDays} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">Pending Amount</h3>
                    <p className="card-amount">
                      {formatCurrency(billingStats.pending_amount || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-4 mb-3">
                <div className="billing-card overdue-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faCreditCard} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">Overdue Amount</h3>
                    <p className="card-amount">
                      {formatCurrency(billingStats.overdue_amount || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Search */}
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="search-box">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search bills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="col-md-3">
              <CustomButton
                text="Export"
                variant="primary"
                className="w-100"
                onClick={() => console.log('Export bills')}
              />
            </div>
          </div>

          {/* Bills Table */}
          <div className="row">
            <div className="col-12">
              {BillsLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="billing-table-container">
                  <CustomTable headers={tableHeaders}>
                    <tbody>
                      {filteredBills.length > 0 ? (
                        filteredBills.map((bill, index) => (
                          <tr key={index} className="bill-row">
                            <td>
                              <strong>{bill.bill_number}</strong>
                            </td>
                            <td>{bill.property_name}</td>
                            <td>
                              <strong>{formatCurrency(bill.amount)}</strong>
                            </td>
                            <td>{formatDate(bill.due_date)}</td>
                            <td>{getStatusBadge(bill.status)}</td>
                            <td>
                              <div className="action-buttons">
                                <CustomButton
                                  text="View"
                                  variant="outline-primary"
                                  size="sm"
                                  icon={faEye}
                                  onClick={() => handleViewBill(bill.id)}
                                  className="me-2"
                                />
                                <CustomButton
                                  text="Download"
                                  variant="outline-success"
                                  size="sm"
                                  icon={faDownload}
                                  onClick={() => handleDownloadBill(bill.id)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <p className="text-muted">No bills found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </CustomTable>
                </div>
              )}
            </div>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};
